import { c as _c } from "react/compiler-runtime";
// @ts-ignore
import { useEffect, useState } from "react";
import type { Address } from "viem";
import { useConnection, useReadContract, useReadContracts } from "wagmi";
import { type NftPoolConfig, type PoolId, type RequiredNftPoolChainType, chainSettings, fallbackChainId, fallbackMulticall } from "../constants";
import { stakingAbi } from "../constants/abi";
import { getPoolSettings } from "../util";

/**
 * Custom hook to fetch staked NFT token ID's and pending reward data
 *
 * @param poolId - The ID of the pool.
 * @param balance - The number of staked tokens.
 * @param account - (Optional) The user's account address.
 * @returns An object containing the staked token IDs, pending rewards, and a function to refresh the data.
 */
export function useStakedNftBalance(poolId, balance, account) {
  const $ = _c(38);
  const {
    chainId
  } = useConnection();
  const t0 = chainId as RequiredNftPoolChainType ?? fallbackChainId as RequiredNftPoolChainType;
  let t1;
  if ($[0] !== poolId || $[1] !== t0) {
    t1 = getPoolSettings(t0, poolId);
    $[0] = poolId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const currPool = t1;
  const [stakedTokenIds, setMyStakedTokenIds] = useState(null);
  const [pendingRewards, setPendingRewards] = useState(0);
  const [rewardsAmount, setRewardsAmount] = useState(0);
  let t2;
  if ($[3] !== currPool.address) {
    t2 = {
      address: currPool.address,
      abi: stakingAbi
    };
    $[3] = currPool.address;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const stakingContract = t2 as const;
  const idReads = [];
  const timeReads = [];
  for (let i = 0; i < balance; i++) {
    idReads.push({
      ...stakingContract,
      functionName: "stakedTokens",
      args: [account?.toString(), i]
    });
  }
  const t3 = account && true;
  let t4;
  if ($[5] !== t3) {
    t4 = {
      enabled: t3
    };
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== currPool.address || $[8] !== t4) {
    t5 = {
      address: currPool.address,
      abi: stakingAbi,
      functionName: "rewardsAmount",
      query: t4
    };
    $[7] = currPool.address;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    data: rewardsData,
    refetch: refetchData
  } = useReadContract(t5) as any;
  const t6 = account && balance > 0;
  let t7;
  if ($[10] !== t6) {
    t7 = {
      enabled: t6
    };
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const {
    data: readData
  } = useReadContracts({
    allowFailure: false,
    contracts: idReads,
    multicallAddress: (chainSettings[chainId as keyof typeof chainSettings] as any)?.multicall ?? fallbackMulticall,
    query: t7
  }) as any;
  for (let i_0 = 0; i_0 < balance; i_0++) {
    if (readData) {
      timeReads.push({
        ...stakingContract,
        functionName: "tokenStakedTime",
        args: [account?.toString(), readData[i_0] as any]
      });
    }
  }
  const t8 = account && balance > 0;
  let t9;
  if ($[12] !== t8) {
    t9 = {
      enabled: t8
    };
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const {
    data: timeData,
    refetch
  } = useReadContracts({
    allowFailure: false,
    contracts: timeReads,
    multicallAddress: (chainSettings[chainId as keyof typeof chainSettings] as any)?.multicall ?? fallbackMulticall,
    query: t9
  }) as any;
  let t10;
  if ($[14] !== refetch || $[15] !== refetchData) {
    t10 = () => {
      refetchData();
      refetch();
    };
    $[14] = refetch;
    $[15] = refetchData;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const refreshData = t10;
  let t11;
  if ($[17] !== account || $[18] !== balance || $[19] !== currPool || $[20] !== readData || $[21] !== rewardsAmount || $[22] !== rewardsData || $[23] !== timeData) {
    t11 = () => {
      if (account && rewardsData) {
        setRewardsAmount(Number(rewardsData));
      }
      if (account && readData) {
        const tokenIdArray = [];
        for (let i_1 = 0; i_1 < readData.length; i_1++) {
          tokenIdArray[i_1] = Number(readData[i_1]);
        }
        setMyStakedTokenIds(tokenIdArray);
      }
      if (account && timeData) {
        const tokenRewardsArray = [];
        let totalRewards = 0;
        let timeDuration;
        for (let i_2 = 0; i_2 < timeData.length; i_2++) {
          timeDuration = Date.now() - Number(timeData[i_2]) * 1000;
          if (timeDuration >= 0) {
            tokenRewardsArray[i_2] = (Date.now() - Number(timeData[i_2]) * 1000) * rewardsAmount / 86400000;
            totalRewards = totalRewards + tokenRewardsArray[i_2];
          } else {
            tokenRewardsArray[i_2] = 0;
          }
          tokenRewardsArray[i_2] = Number(timeData[i_2]);
        }
        setPendingRewards(Number(totalRewards / 10 ** currPool.rewardDecimals));
      }
      if (balance === 0) {
        setMyStakedTokenIds([]);
        setPendingRewards(0);
      }
    };
    $[17] = account;
    $[18] = balance;
    $[19] = currPool;
    $[20] = readData;
    $[21] = rewardsAmount;
    $[22] = rewardsData;
    $[23] = timeData;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== account || $[26] !== balance || $[27] !== currPool || $[28] !== poolId || $[29] !== readData || $[30] !== rewardsAmount || $[31] !== rewardsData || $[32] !== timeData) {
    t12 = [readData, timeData, account, poolId, balance, rewardsAmount, rewardsData, currPool];
    $[25] = account;
    $[26] = balance;
    $[27] = currPool;
    $[28] = poolId;
    $[29] = readData;
    $[30] = rewardsAmount;
    $[31] = rewardsData;
    $[32] = timeData;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  useEffect(t11, t12);
  let t13;
  if ($[34] !== pendingRewards || $[35] !== refreshData || $[36] !== stakedTokenIds) {
    t13 = {
      stakedTokenIds,
      pendingRewards,
      refreshData
    };
    $[34] = pendingRewards;
    $[35] = refreshData;
    $[36] = stakedTokenIds;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  return t13;
}